import { create } from 'zustand';
import type { Dance, Order, KeyframeInterval, EditMode } from '../types';
export const ROBOT_IMAGES = [
  'https://public.youware.com/users-website-assets/prod/4ee23aea-171c-4f70-a43c-35faf426cad0/eb4089b8d80d4d83a662763900ef16db.png',
  'https://public.youware.com/users-website-assets/prod/4ee23aea-171c-4f70-a43c-35faf426cad0/5a2f3f7c51bd40b68e6a9a6f507ae9cc.png',
  'https://public.youware.com/users-website-assets/prod/4ee23aea-171c-4f70-a43c-35faf426cad0/7ccdbb596d0b48008bc41907189aaeb4.png',
  'https://public.youware.com/users-website-assets/prod/4ee23aea-171c-4f70-a43c-35faf426cad0/3ed1f667490045959a6b499819553df8.png',
];

export const ROBOT_IMAGE_TRANSFORM = 'rotate(90deg)';

const initialDances: Dance[] = [
  { id: 'd1', name: '霍元甲舞蹈', type: 'simulation', status: 'success', duration: '00:45', robotModel: 'Unitree H2', imageUrl: ROBOT_IMAGES[0], videoUrl: '/assets/dance-preview.mp4', createdAt: '2025-03-25 14:30' },
  { id: 'd2', name: '恭喜发财', type: 'simulation', status: 'generating', duration: '00:30', robotModel: 'Unitree H2', imageUrl: ROBOT_IMAGES[1], progress: 45, createdAt: '2025-03-25 15:10' },
  { id: 'd3', name: '机械舞', type: 'simulation', status: 'failed', duration: '01:00', robotModel: 'Unitree R1', imageUrl: ROBOT_IMAGES[2], createdAt: '2025-03-24 10:00' },
  { id: 'd4', name: '街舞基础', type: 'simulation', status: 'success', duration: '00:50', robotModel: 'Unitree H2', imageUrl: ROBOT_IMAGES[3], createdAt: '2025-03-23 16:20' },
  { id: 'd5', name: '古风舞', type: 'simulation', status: 'generating', duration: '00:40', robotModel: '智元远征A2', imageUrl: ROBOT_IMAGES[0], progress: 72, createdAt: '2025-03-25 13:00' },
];

const initialOrders: Order[] = [
  { id: 'o1', orderNo: 'OD1234567890', type: 'simulation', typeLabel: '生成仿真视频服务', amount: 100, status: 'pending', danceName: '霍元甲舞蹈', robotModel: 'Unitree H2', createdAt: '2025-03-25 14:30' },
  { id: 'o2', orderNo: 'OD1234567891', type: 'simulation', typeLabel: '生成仿真视频服务', amount: 30, status: 'processing', danceName: '恭喜发财', robotModel: 'Unitree H2', createdAt: '2025-03-25 13:20' },
  { id: 'o3', orderNo: 'OD1234567892', type: 'training', typeLabel: '训练舞蹈服务', amount: 70, status: 'completed', danceName: '机械舞', robotModel: 'Unitree H2', createdAt: '2025-03-24 15:30' },
  { id: 'o4', orderNo: 'OD1234567893', type: 'simulation', typeLabel: '生成仿真视频服务', amount: 30, status: 'closed', danceName: '街舞基础', robotModel: 'Unitree R1', createdAt: '2025-03-23 10:15', closeReason: '支付超时' },
];

const initialIntervals: KeyframeInterval[] = [];

interface AuthState {
  isLoggedIn: boolean;
  user: { phone: string; nickname: string } | null;
  login: (phone: string, password: string) => boolean;
  register: (phone: string, password: string, nickname: string) => boolean;
  logout: () => void;
}

const USER_INFO_KEY = 'user_info';

/** 从 localStorage 恢复用户信息（刷新页面后 user 不丢失） */
function loadStoredUser(): AuthState['user'] {
  try {
    const raw = localStorage.getItem(USER_INFO_KEY);
    return raw ? (JSON.parse(raw) as AuthState['user']) : null;
  } catch {
    return null;
  }
}

function persistUser(user: AuthState['user']): void {
  try {
    if (user) localStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_INFO_KEY);
  } catch {
    /* 存储失败静默处理 */
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  isLoggedIn: false,
  user: loadStoredUser(),
  login: (phone, _password) => {
    if (phone.length >= 11) {
      const user = { phone, nickname: 'ODance用户' };
      persistUser(user);
      set({ isLoggedIn: true, user });
      return true;
    }
    return false;
  },
  register: (phone, _password, nickname) => {
    if (phone.length >= 11 && nickname.length > 0) {
      const user = { phone, nickname };
      persistUser(user);
      set({ isLoggedIn: true, user });
      return true;
    }
    return false;
  },
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    persistUser(null);
    set({ isLoggedIn: false, user: null });
  },
}));

interface AppStore {
  dances: Dance[];
  addDance: (dance: Dance) => void;
  updateDance: (id: string, updates: Partial<Dance>) => void;
  deleteDance: (id: string) => void;
  orders: Order[];
  addOrder: (order: Order) => void;
  updateOrder: (id: string, updates: Partial<Order>) => void;
  keyframeIntervals: KeyframeInterval[];
  addKeyframeInterval: (interval: KeyframeInterval) => void;
  updateKeyframeInterval: (id: string, updates: Partial<KeyframeInterval>) => void;
  deleteKeyframeInterval: (id: string) => void;
  clearKeyframeIntervals: () => void;
  selectedIntervalId: string | null;
  setSelectedIntervalId: (id: string | null) => void;
  editMode: EditMode;
  setEditMode: (mode: EditMode) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export const useStore = create<AppStore>((set) => ({
  dances: initialDances,
  addDance: (dance) => set((state) => ({ dances: [dance, ...state.dances] })),
  updateDance: (id, updates) => set((state) => ({ dances: state.dances.map((d) => (d.id === id ? { ...d, ...updates } : d)) })),
  deleteDance: (id) => set((state) => ({ dances: state.dances.filter((d) => d.id !== id) })),
  orders: initialOrders,
  addOrder: (order) => set((state) => ({ orders: [order, ...state.orders] })),
  updateOrder: (id, updates) => set((state) => ({ orders: state.orders.map((o) => (o.id === id ? { ...o, ...updates } : o)) })),
  keyframeIntervals: initialIntervals,
  addKeyframeInterval: (interval) => set((state) => ({ keyframeIntervals: [...state.keyframeIntervals, interval] })),
  updateKeyframeInterval: (id, updates) => set((state) => ({
    keyframeIntervals: state.keyframeIntervals.map((ki) => (ki.id === id ? { ...ki, ...updates } : ki)),
  })),
  deleteKeyframeInterval: (id) => set((state) => ({
    keyframeIntervals: state.keyframeIntervals.filter((ki) => ki.id !== id),
    selectedIntervalId: state.selectedIntervalId === id ? null : state.selectedIntervalId,
  })),
  clearKeyframeIntervals: () => set({ keyframeIntervals: [], selectedIntervalId: null }),
  selectedIntervalId: null,
  setSelectedIntervalId: (id) => set({ selectedIntervalId: id }),
  editMode: 'currentFrame' as EditMode,
  setEditMode: (mode) => set({ editMode: mode }),
  sidebarCollapsed: false,
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
}));
